import React, { useState, useEffect, useCallback } from 'react'
import { API, graphqlOperation } from 'aws-amplify'
import styled from 'styled-components'
import {
  Dialog,
  Form,
  Input,
  Checkbox,
  Button,
  Loading,
  Notification,
  Message,
  MessageBox,
} from 'element-react'
import { listAddresses } from '../graphql/queries'
import { createAddress, updateAddress, deleteAddress } from '../graphql/mutations'

const emptyForm = {
  label: '',
  fullName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
  phoneNumber: '',
  isDefault: false,
}

const Wrap = styled.div``

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
`;

const AddBtn = styled.button`
  flex: 0 0 auto;
  background: #01bf71;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.88rem;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #04a763;
  }
`;

const Empty = styled.div`
  background: #fff;
  border: 1px dashed rgba(31, 45, 61, 0.18);
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  color: #6a7b8a;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const CardLabel = styled.div`
  font-weight: 800;
  color: #1f2d3d;
`;

const Pill = styled.span`
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
`;

const SetDefaultLink = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: none;
  color: #0a8f57;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const CardName = styled.div`
  color: #1f2d3d;
  font-weight: 600;
  margin-bottom: 2px;
`;

const CardLine = styled.div`
  color: #6a7b8a;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
`;

const EditBtn = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: 1px solid rgba(1, 191, 113, 0.5);
  color: #0a8f57;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 6px 16px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(1, 191, 113, 0.08);
  }
`;

const DeleteBtn = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: 1px solid rgba(239, 83, 80, 0.6);
  color: #d32f2f;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 6px 16px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: #ef5350;
    color: #fff;
  }
`;

const AddressBook = () => {
  const [addresses, setAddresses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [dialogVisible, setDialogVisible] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)

  const fetchAddresses = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await API.graphql(graphqlOperation(listAddresses, { limit: 50 }))
      setAddresses(result.data.listAddresses.items || [])
    } catch (error) {
      console.error('Error fetching addresses', error)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchAddresses()
  }, [fetchAddresses])

  const openNewDialog = () => {
    setEditingId(null)
    setForm(emptyForm)
    setDialogVisible(true)
  }

  const openEditDialog = address => {
    setEditingId(address.id)
    setForm({
      label: address.label,
      fullName: address.fullName,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country,
      phoneNumber: address.phoneNumber,
      isDefault: !!address.isDefault,
    })
    setDialogVisible(true)
  }

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const clearPreviousDefault = async () => {
    const previousDefault = addresses.find(a => a.isDefault && a.id !== editingId)
    if (previousDefault) {
      await API.graphql(
        graphqlOperation(updateAddress, { input: { id: previousDefault.id, isDefault: false } })
      )
    }
  }

  const handleSave = async () => {
    const { label, fullName, addressLine1, city, state, zip, country, phoneNumber } = form
    if (!label || !fullName || !addressLine1 || !city || !state || !zip || !country || !phoneNumber) {
      Message({ type: 'error', message: 'Please fill in all required fields.' })
      return
    }

    try {
      if (form.isDefault) {
        await clearPreviousDefault()
      }

      if (editingId) {
        await API.graphql(graphqlOperation(updateAddress, { input: { id: editingId, ...form } }))
      } else {
        await API.graphql(graphqlOperation(createAddress, { input: form }))
      }

      Notification({
        title: 'Success',
        message: editingId ? 'Address updated' : 'Address added',
        type: 'success',
        duration: 2000,
      })
      setDialogVisible(false)
      fetchAddresses()
    } catch (error) {
      console.error('Error saving address', error)
      Notification.error({ title: 'Error', message: error.message || 'Could not save address' })
    }
  }

  const handleDelete = address => {
    MessageBox.confirm(`Delete "${address.label}"?`, 'Remove address', {
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      type: 'warning',
    })
      .then(async () => {
        try {
          await API.graphql(graphqlOperation(deleteAddress, { input: { id: address.id } }))
          Notification({ title: 'Deleted', message: 'Address removed', type: 'success', duration: 2000 })
          fetchAddresses()
        } catch (error) {
          console.error('Error deleting address', error)
          Notification.error({ title: 'Error', message: error.message || 'Could not delete address' })
        }
      })
      .catch(() => {})
  }

  const handleSetDefault = async address => {
    try {
      const previousDefault = addresses.find(a => a.isDefault && a.id !== address.id)
      if (previousDefault) {
        await API.graphql(
          graphqlOperation(updateAddress, { input: { id: previousDefault.id, isDefault: false } })
        )
      }
      await API.graphql(graphqlOperation(updateAddress, { input: { id: address.id, isDefault: true } }))
      fetchAddresses()
    } catch (error) {
      console.error('Error setting default address', error)
    }
  }

  if (isLoading) return <Loading />

  return (
    <Wrap>
      <Header>
        <Title>Saved addresses</Title>
        <AddBtn type="button" onClick={openNewDialog}>
          + Add address
        </AddBtn>
      </Header>

      {addresses.length === 0 && (
        <Empty>No saved addresses yet. Add one to speed up checkout.</Empty>
      )}

      <List>
        {addresses.map(address => (
          <Card key={address.id}>
            <CardHeader>
              <CardLabel>{address.label}</CardLabel>
              {address.isDefault ? (
                <Pill>Default</Pill>
              ) : (
                <SetDefaultLink type="button" onClick={() => handleSetDefault(address)}>
                  Set as default
                </SetDefaultLink>
              )}
            </CardHeader>
            <CardName>{address.fullName}</CardName>
            <CardLine>
              {address.addressLine1}
              {address.addressLine2 ? `, ${address.addressLine2}` : ''}
            </CardLine>
            <CardLine>
              {address.city}, {address.state} {address.zip}, {address.country}
            </CardLine>
            <CardLine>{address.phoneNumber}</CardLine>
            <CardActions>
              <EditBtn type="button" onClick={() => openEditDialog(address)}>
                Edit
              </EditBtn>
              <DeleteBtn type="button" onClick={() => handleDelete(address)}>
                Delete
              </DeleteBtn>
            </CardActions>
          </Card>
        ))}
      </List>

      <Dialog
        size="large"
        customClass="dialog"
        title={editingId ? 'Edit address' : 'Add address'}
        visible={dialogVisible}
        onCancel={() => setDialogVisible(false)}
      >
        <Dialog.Body>
          <Form labelPosition="top">
            <Form.Item label="Label (e.g. Home, Work)">
              <Input value={form.label} onChange={v => handleFieldChange('label', v)} placeholder="Home" />
            </Form.Item>
            <Form.Item label="Full name">
              <Input value={form.fullName} onChange={v => handleFieldChange('fullName', v)} placeholder="Jane Doe" />
            </Form.Item>
            <Form.Item label="Address line 1">
              <Input
                value={form.addressLine1}
                onChange={v => handleFieldChange('addressLine1', v)}
                placeholder="185 Berry St."
              />
            </Form.Item>
            <Form.Item label="Address line 2 (optional)">
              <Input
                value={form.addressLine2}
                onChange={v => handleFieldChange('addressLine2', v)}
                placeholder="Suite 550"
              />
            </Form.Item>
            <Form.Item label="City">
              <Input value={form.city} onChange={v => handleFieldChange('city', v)} placeholder="San Francisco" />
            </Form.Item>
            <Form.Item label="State">
              <Input value={form.state} onChange={v => handleFieldChange('state', v)} placeholder="California" />
            </Form.Item>
            <Form.Item label="ZIP">
              <Input value={form.zip} onChange={v => handleFieldChange('zip', v)} placeholder="94103" />
            </Form.Item>
            <Form.Item label="Country">
              <Input value={form.country} onChange={v => handleFieldChange('country', v)} placeholder="USA" />
            </Form.Item>
            <Form.Item label="Phone">
              <Input
                value={form.phoneNumber}
                onChange={v => handleFieldChange('phoneNumber', v)}
                placeholder="333-444-5555"
              />
            </Form.Item>
            <Checkbox checked={form.isDefault} onChange={v => handleFieldChange('isDefault', v)}>
              Set as default address
            </Checkbox>
          </Form>
        </Dialog.Body>
        <Dialog.Footer>
          <Button onClick={() => setDialogVisible(false)}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </Dialog.Footer>
      </Dialog>
    </Wrap>
  )
}

export default AddressBook
